import { IInterestResponse } from '../../pages/Root/services/api/interest/interest-model-definitions.ts';

export type LightboxStatus = {
  http: number;
  lkbk: string;
};

export type LightboxLanguageText = {
  [languageCode: string]: string;
};

export type LightboxInterest = {
  name: LightboxLanguageText;
  weight: number;
};

export type LightboxInterests = Record<string, LightboxInterest>;

export type LightboxImage = {
  link: string;
  width: number;
  height: number;
};

export type LightboxPreview = LightboxImage & {
  aspect: string;
};

export type LightboxKB = {
  startX: number;
  startY: number;
  startB: number;
  endX: number;
  endY: number;
  endB: number;
};

export type LightboxItem = {
  title: LightboxLanguageText;
  summary: LightboxLanguageText;
  link: string;
  sourceLink: string;
  uid: string;
  lbtype: string;
  interests: string[];
  images: LightboxImage[];
  previews: LightboxPreview[];
  kb: LightboxKB;
  owner: string;
  ownerUID: string;
  brandLogo: string;
  brandLogoDark: string;
  numImages: number;
  boost: number;
  promote: boolean;
  publishOn: string;
  scheduledOn: string;
};

export class EnhancedLightboxItem implements LightboxItem {
  constructor(lt: LightboxItem) {
    Object.assign(this, lt);
  }

  title!: LightboxLanguageText;
  summary!: LightboxLanguageText;
  link!: string;
  sourceLink!: string;
  uid!: string;
  lbtype!: string;
  interests!: string[];
  images!: LightboxImage[];
  previews!: LightboxPreview[];
  kb!: LightboxKB;
  owner!: string;
  ownerUID!: string;
  brandLogo!: string;
  brandLogoDark!: string;
  numImages!: number;
  boost!: number;
  promote!: boolean;
  publishOn!: string;
  scheduledOn!: string;

  public linkToContent = (partnerOverride: string | null) => {
    switch (this.lbtype) {
      case 'link':
        return this.link;
      case 'article':
        // removed in CMBU-2029, may be reintroduced later so keeping here for now
        // return getPartner(partnerOverride) === 'moment'
        //   ? this.link.replace(/\/lookbook\//, '/stories/')
        //   : this.link;
        return this.link;
      case 'springboard':
        return this.sourceLink;
      case 'product':
        return this.sourceLink;
    }
    return this.link;
  };
}

export type LightboxRootObject = {
  status: LightboxStatus;
  ver: string;
  interests: LightboxInterests;
  items: LightboxItem[];
};

export type PublisherAPIResponse = {
  status: LightboxStatus;
  ver: string;
  interests: LightboxInterests;
  items: LightboxItem[];
};

export type FeedQueryResult = {
  news: LightboxRootObject;
  interests: IInterestResponse;
};

export type PublisherQueryResult = FeedQueryResult;

export const adjustFotoscapesLink = (link: string) => {
  try {
    const regex = /^https?:\/\/(staging.)?(fotoscapes|get-moment).com(\/.*)$/;
    const match = link.match(regex);
    if (match) {
      // now converting all links to intents to launch in new browser window
      // const path = match[3];
      // return `${path}`;
    }
  } catch (_: unknown) {
    /* empty */
  }
  return link;
};

// Single lookbook API response

export interface OwnedBy {
  name: string;
  uid: string;
  isBrand: boolean;
  logo: string;
  logoLarge: string;
  logoDark: string;
  logoAlpha: number;
  thumbnail: string;
  brandGroup: string;
}

export interface Pane {
  translations?: LightboxLanguageText;
  lookUid?: string;
}

export interface View {
  viewType: 'two-pane' | 'one-pane'; // You can extend this with other known view types
  min_y?: number; // Optional, as it might not be present for all viewTypes (e.g., one-pane)
  max_y?: number; // Optional
  panes: Pane[];
}

export interface Plan {
  ver: string;
  views: View[];
}

export interface Interest {
  name: string;
  uid: string;
  level: number;
}

export interface KenBurnsConfig {
  startX: number;
  startY: number;
  startB: number;
  endX: number;
  endY: number;
  endB: number;
}

export interface KenBurnsParams {
  [key: string]: KenBurnsConfig; // For dynamic keys like "0:0", "1:1", etc.
}

export interface PreviewConfig {
  centerX: number;
  centerY: number;
  breadth: number;
}

export interface PreviewParams {
  [key: string]: PreviewConfig; // For dynamic keys like "1:1", "9:4", etc.
}

export interface Image {
  height: number;
  width: number;
  url: string;
}

export interface Thumbnail {
  height: number;
  width: number;
  url: string;
  aspect: string;
}

export interface Look {
  uid: string;
  title: string;
  averageColor: string;
  kenBurnsParams: KenBurnsParams;
  previewParams: PreviewParams;
  images: Image[];
  thumbnails: Thumbnail[];
  ownedBy: OwnedBy;
  updatedAt: string; // ISO date string
  credit: string;
}

export interface Tag {
  uid: string;
  name: string;
  category: string;
}

export interface DefaultAdTag {
  unitPath: string;
  pubwisePreScript: string;
  pubwiseScript: string;
}

export interface Lookbook {
  title: LightboxLanguageText;
  shortTitle: LightboxLanguageText;
  ownedBy: OwnedBy;
  uid: string;
  summary: LightboxLanguageText;
  credit: LightboxLanguageText;
  qcc: boolean;
  plan: Plan;
  lbtype: string;
  interests: Interest[];
  looks: Look[];
  coverUID: string;
  previewUids: string[];
  tags: Tag[];
  link: string;
  sourceLink?: string;
  updatedAt: string; // ISO date string
  createdAt: string; // ISO date string
  publishOn: string; // ISO date string
  defaultAdTag: DefaultAdTag;
}

export interface LookbookAPIResponse {
  status: LightboxStatus;
  lookbook: Lookbook;
  gen: string; // ISO date string
  ttl: string; // ISO date string
}

export interface IThumbnail {
  height: number;
  url: string;
  width: number;
}

export interface IRecircLook {
  averageColor: string;
  thumbnails: IThumbnail[];
}

export interface IRecircLookBook {
  interest: string;
  link: string;
  looks: IRecircLook[];
  summary: Record<string, string>;
  title: Record<string, string>;
  uid: string;
}

export interface PersonalizationInterest {
  name: LightboxLanguageText;
  weight: number;
}

export interface PersonalizationItem {
  title: LightboxLanguageText;
  shortTitle?: LightboxLanguageText;
  summary: LightboxLanguageText;
  link: string;
  sourceLink?: string;
  uid: string;
  lbtype: string;
  interests: string[];
  images: LightboxImage[];
  previews: LightboxPreview[];
  kb: LightboxKB;
  owner: string;
  ownerUID: string;
  brandLogo: string;
  brandLogoDark: string;
  numImages: number;
  boost: number;
  promote: boolean;
  publishOn: string;
}

export interface PersonalizationAPIResponse {
  status: {
    http: number;
    lkbk: string;
  };
  ver: string;
  interests: Record<string, PersonalizationInterest>;
  items: PersonalizationItem[];
  gen: string; // ISO date string
  ttl: string; // ISO date string
}

export interface RecircAPIResponse {
  status: LightboxStatus;
  lookbooks: IRecircLookBook[];
  gen: string; // ISO date string
  ttl: string; // ISO date string
}

export interface GalleryAPIResponse {
  lookbookResponse: LookbookAPIResponse;
  recircResponse?: LightboxRootObject;
}

export interface ProductAPIResponse {
  status: LightboxStatus;
  items: Product[];
  gen: string; // ISO date string
  ttl: string; // ISO date string
}

export interface ProductPurchase {
  cta: LightboxLanguageText;
  brand: string;
  price: number;
  currency: string; // ISO currency code
}

export interface Product {
  title: LightboxLanguageText;
  shortTitle?: LightboxLanguageText;
  summary: LightboxLanguageText;
  link: string;
  sourceLink?: string;
  uid: string;
  lbtype: string;
  interests: string[];
  images: LightboxImage[];
  previews: LightboxPreview[];
  kb: LightboxKB;
  owner: string;
  ownerUID: string;
  brandLogo: string;
  brandLogoDark: string;
  numImages: number;
  boost: number;
  promote: boolean;
  publishOn: string;
  product: ProductPurchase;
}
